import type {
	FieldConfiguration,
	ListBuilderColumn,
	SortField,
	StyleConfiguration,
} from '../../types';
import { MERCHANDISER_STATUS_VALUE_MAPPING } from './shared';

/*
 * PD-16707 — field builders shared by the single-value columns (Status,
 * Health, Sensor, Locations, Fill %, Qty Needed, Temp) and the stacked
 * Status / Sensor / Fill columns that replaced them in the default layout.
 * A value reads the same wherever it is shown, and its rules (a dash for an
 * unknowable value, the warning icons) live in one place.
 */

/** Separates the stacked values of one cell onto their own lines. */
const lineBreakField = (id: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	displayStyle: 'line_break',
});

/** A secondary line under a cell's main value, styled like the make/model line. */
const secondaryLineStyle = (): StyleConfiguration => ({
	size: 'sm',
	colorRule: { type: 'muted' },
});

/** Status badge. */
const statusBadgeField = (): FieldConfiguration => ({
	id: 'status-field',
	dataConfig: {
		type: 'field',
		field: { fieldName: 'status', objectTable: 'merchandiser' },
	},
	styleConfig: {
		size: 'sm',
		valueMapping: [...MERCHANDISER_STATUS_VALUE_MAPPING],
	},
	displayStyle: 'badge',
});

/** Sensor-driven health badge (see createHealthColumn). */
const healthBadgeField = (): FieldConfiguration => ({
	id: 'health-field',
	dataConfig: {
		type: 'field',
		field: { fieldName: 'healthState', objectTable: 'merchandiser' },
	},
	styleConfig: {
		size: 'sm',
		valueMapping: [
			{
				value: 'healthy',
				label: 'Healthy',
				color: { type: 'success' },
			},
			{
				value: 'needs_attention',
				label: 'Needs Attention',
				color: { type: 'warning' },
			},
			{
				value: 'offline',
				label: 'Offline',
				color: { type: 'error' },
			},
		],
	},
	displayStyle: 'badge',
});

/** The attached sensor's own device id, or a dash when there is none. */
const sensorDeviceField = (): FieldConfiguration => ({
	id: 'sensor-device-field',
	dataConfig: {
		type: 'field',
		field: { fieldName: 'sensorDeviceLabel', objectTable: 'merchandiser' },
	},
	styleConfig: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'text',
	emptyValue: '-',
});

/** Number of locations, or a dash when none are configured (see createBinsColumn). */
const locationsCountField = (): FieldConfiguration => ({
	id: 'bins-field',
	dataConfig: {
		type: 'conditional',
		conditions: [
			{
				field: 'locationsCount',
				// Compared as strings by the conditional resolver.
				operator: 'not_equals',
				value: '0',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'locationsCount',
						objectTable: 'merchandiser',
					},
				},
			},
		],
		defaultDataConfig: { type: 'custom_text', customText: '—' },
	},
	styleConfig: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'text',
});

/**
 * Warning icon for a unit at or below the refill threshold. `floatingLeft`
 * pins it before a one-line Fill % cell; in the stacked Fill cell it follows
 * the percentage instead, because a floating icon indents every line of the
 * cell and leaves rows with and without it misaligned (PD-16707).
 */
const needsRefillIconField = (floatingLeft: boolean): FieldConfiguration => ({
	id: 'fill-warning-icon',
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: {
		size: 'sm',
		iconName: 'triangle-exclamation',
		colorRule: { type: 'static', staticColor: { type: 'warning' } },
		tooltip: 'Needs refill',
	},
	displayStyle: 'icon',
	conditionalVisibility: {
		field: 'refillNeeded',
		condition: 'is_not_null',
	},
	floatingLeft,
});

/**
 * Fill % with its percent sign (a bare "0" over "Qty needed 10" read as a
 * count), or a dash when it is unknowable (no sized bins). The sign is in the
 * template rather than a field suffix, which would also follow the dash.
 */
const fillPercentField = (): FieldConfiguration => ({
	id: 'fill-field',
	dataConfig: {
		type: 'conditional',
		conditions: [
			{
				field: 'fillPercentage',
				operator: 'not_equals',
				value: null,
				dataConfig: {
					type: 'template',
					template: '{merchandiser.fillPercentage}%',
					templateFields: [
						{
							fieldName: 'fillPercentage',
							objectTable: 'merchandiser',
						},
					],
				},
			},
		],
		defaultDataConfig: { type: 'custom_text', customText: '—' },
	},
	styleConfig: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'text',
});

/** Fill % low to high: the emptiest units first. */
const fillSortFields = (): Array<SortField> => [
	{
		fieldName: 'fillPercentage',
		direction: 'ASC',
		objectTable: 'merchandiser',
	},
];

/** Whole units short of capacity, or a dash when unknowable. */
const quantityNeededField = (): FieldConfiguration => ({
	id: 'quantity-needed-field',
	dataConfig: {
		type: 'conditional',
		conditions: [
			{
				field: 'quantityNeeded',
				operator: 'not_equals',
				value: null,
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'quantityNeeded',
						objectTable: 'merchandiser',
					},
				},
			},
		],
		defaultDataConfig: { type: 'custom_text', customText: '—' },
	},
	styleConfig: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'text',
});

/**
 * Warning icon for an open sensor alert (temperature/offline), shown before
 * the temperature reading. The alert detail stays on the unit's Issues tab.
 * `floatingLeft` pins it beside a cell's FIRST line, which is right when the
 * reading leads the cell and wrong when the reading is a lower line.
 */
const openAlertIconField = (floatingLeft: boolean): FieldConfiguration => ({
	id: 'temperature-warning-icon',
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: {
		size: 'sm',
		iconName: 'triangle-exclamation',
		colorRule: { type: 'static', staticColor: { type: 'warning' } },
		tooltip: 'Open sensor alert',
	},
	displayStyle: 'icon',
	conditionalVisibility: {
		field: 'alertSummary',
		condition: 'is_not_null',
	},
	floatingLeft,
});

/** The current temperature in °F, or a dash when there is no current reading. */
const temperatureField = (): FieldConfiguration => ({
	id: 'temperature-field',
	dataConfig: {
		// A bare template renders a dangling "°F" when there is no
		// reading — the presence-checked conditional shows "—" instead.
		type: 'conditional',
		conditions: [
			{
				field: 'currentTemperature',
				operator: 'not_equals',
				value: null,
				dataConfig: {
					type: 'template',
					template: '{merchandiser.currentTemperature}°F',
					templateFields: [
						{
							fieldName: 'currentTemperature',
							objectTable: 'merchandiser',
						},
					],
				},
			},
		],
		defaultDataConfig: { type: 'custom_text', customText: '—' },
	},
	styleConfig: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'text',
});

/** Merchandiser name (link to detail) with manufacturer/model subtext. */
export const createMerchandiserNameColumn = (): ListBuilderColumn => ({
	id: 'merchandiser-name',
	headers: [{ id: 'merchandiser-name-header', text: 'Merchandiser' }],
	fields: [
		{
			id: 'name-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'name', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		// PD-14339 — managed-catalog make/model shown together as subtext
		// (e.g. "True TDD-2"). PD-16707 — one joined value, and the line only
		// when there is one: two separate fields each fell back to a dash, so a
		// unit with neither set showed a stray "- -" line.
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
			conditionalVisibility: {
				field: 'makeModelLabel',
				condition: 'is_not_null',
			},
		},
		{
			id: 'make-model-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'makeModelLabel', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'makeModelLabel',
				condition: 'is_not_null',
			},
		},
		// PD-15485 — the serial plate, on its own line under make/model. It is
		// how a unit is identified in the field, and the list could neither
		// show it nor be searched by it. Hidden entirely when absent: units are
		// regularly received before their serial plate is available, and a
		// bare "S/N" label on an empty value reads as missing data.
		{
			id: 'serial-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
			conditionalVisibility: {
				field: 'serialNumber',
				condition: 'is_not_null',
			},
		},
		{
			id: 'serial-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'serialNumber', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
			prefix: 'S/N ',
			conditionalVisibility: {
				field: 'serialNumber',
				condition: 'is_not_null',
			},
		},
	],
	sortable: true,
});

/**
 * PD-15485 — the sensor hardware attached to the unit, by the device's own
 * id (the label printed on it), so a row can be matched to hardware in hand.
 */
export const createSensorDeviceColumn = (): ListBuilderColumn => ({
	id: 'sensor-device',
	headers: [{ id: 'sensor-device-header', text: 'Sensor' }],
	fields: [sensorDeviceField()],
	sortable: false,
});

/** Secondary address lines, styled like the client list's address lines. */
const placementAddressStyle = (): StyleConfiguration => ({
	size: 'sm',
	colorRule: { type: 'theme', themeColor: 'textSecondary' },
});

/**
 * The customer (or warehouse) the unit sits at, shown the way the client list
 * shows a customer: the name, linked to its page, then the street and the
 * city / state / zip on their own lines (PD-16707). It was a single
 * "Client - street, city, state, zip" string under "Placement" — Motor City
 * Ice, the tenant that runs merchandisers, had renamed it Customer Name and
 * moved it first, which is now the standard layout.
 *
 * The name renders through one of two fields so each can carry its own link:
 * a customer placement opens the client, a warehouse placement opens the
 * warehouse. An unplaced unit shows a dash and links nowhere.
 */
export const createPlacementColumn = (): ListBuilderColumn => ({
	id: 'placement',
	headers: [{ id: 'placement-header', text: 'Customer Name' }],
	fields: [
		{
			id: 'placement-client-name',
			link: {
				enabled: true,
				type: 'custom',
				urlTemplate: '/clients/{clientId}',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'placementName', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'placementType',
				condition: 'equals',
				value: 'client',
			},
		},
		{
			id: 'placement-warehouse-name',
			link: {
				enabled: true,
				type: 'custom',
				urlTemplate: '/warehouses/{warehouseId}',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'placementName', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'placementType',
				condition: 'equals',
				value: 'warehouse',
			},
		},
		{
			id: 'placement-unplaced',
			dataConfig: { type: 'custom_text', customText: '-' },
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
			conditionalVisibility: { field: 'placementType', condition: 'is_null' },
		},
		// Each address line hides with its leading line break when the part is
		// missing, so a sparse address never leaves a blank line or a stray
		// comma (a template with no values would otherwise print "-").
		{
			id: 'placement-street-break',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
			conditionalVisibility: {
				field: 'placementAddress1',
				condition: 'is_not_null',
			},
		},
		{
			id: 'placement-street',
			dataConfig: {
				type: 'template',
				template: '{placementAddress1} {placementAddress2}',
				templateFields: [
					{ fieldName: 'placementAddress1', objectTable: 'merchandiser' },
					{ fieldName: 'placementAddress2', objectTable: 'merchandiser' },
				],
			},
			styleConfig: placementAddressStyle(),
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'placementAddress1',
				condition: 'is_not_null',
			},
		},
		{
			id: 'placement-city-break',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
			conditionalVisibility: {
				field: 'placementCity',
				condition: 'is_not_null',
			},
		},
		{
			id: 'placement-city-state-zip',
			dataConfig: {
				type: 'template',
				template: '{placementCity}, {placementState} {placementZip}',
				templateFields: [
					{ fieldName: 'placementCity', objectTable: 'merchandiser' },
					{ fieldName: 'placementState', objectTable: 'merchandiser' },
					{ fieldName: 'placementZip', objectTable: 'merchandiser' },
				],
			},
			styleConfig: placementAddressStyle(),
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'placementCity',
				condition: 'is_not_null',
			},
		},
	],
	sortable: false,
	minWidth: 220,
});

/** Status badge. */
export const createStatusColumn = (): ListBuilderColumn => ({
	id: 'status',
	headers: [{ id: 'status-header', text: 'Status' }],
	fields: [statusBadgeField()],
	sortable: true,
	width: 130,
});

/**
 * Number of locations (child locations) the merchandiser owns. A unit with
 * none configured shows a dash — "0" reads as a measured value on a unit we
 * actually know nothing about (owner request, 2026-08-24).
 */
export const createBinsColumn = (): ListBuilderColumn => ({
	id: 'bins',
	headers: [{ id: 'bins-header', text: 'Locations' }],
	fields: [locationsCountField()],
	sortable: false,
	width: 90,
});

/**
 * Fill % rolled up across the merchandiser's locations. Null (no sized bins
 * → unknowable) renders a dash. The former separate Stock column is folded
 * into the leading warning icon: shown when the unit needs a refill, keyed
 * off the row's `refillNeeded` flag (PD-16059 — at or below the tenant's
 * refill threshold, not a fixed Empty/Low bucket).
 *
 * PD-16059 — sortable. `fillPercentage` is computed after the query, so the
 * backend orders the whole filtered set in memory before paginating (nulls
 * last in both directions); the header click just names the field.
 */
export const createFillColumn = (): ListBuilderColumn => ({
	id: 'fill',
	headers: [{ id: 'fill-header', text: 'Fill %' }],
	fields: [needsRefillIconField(true), fillPercentField()],
	sortable: true,
	sortFields: fillSortFields(),
	width: 100,
});

/**
 * PD-16059 — whole units short of capacity (capacity minus on hand), so
 * staff can see how much ice a unit takes at a glance. Null (no sized bins
 * → unknowable) renders a dash. Sortable the same way as Fill %: computed
 * post-query and ordered in memory server-side; the default direction is
 * high to low because "who needs the most" is the question being asked.
 */
export const createQuantityNeededColumn = (): ListBuilderColumn => ({
	id: 'quantity-needed',
	headers: [{ id: 'quantity-needed-header', text: 'Qty Needed' }],
	fields: [quantityNeededField()],
	sortable: true,
	sortFields: [
		{
			fieldName: 'quantityNeeded',
			direction: 'DESC',
			objectTable: 'merchandiser',
		},
	],
	width: 100,
});

/**
 * PD-15416 — the unit's average zone temperature (°F, bubbled from the
 * attached sensor device's depth modules at ingestion). Blank when the
 * merchandiser has no reporting sensors.
 */
export const createTemperatureColumn = (): ListBuilderColumn => ({
	id: 'temperature',
	headers: [{ id: 'temperature-header', text: 'Temp' }],
	// The former Alerts column, folded in: an open sensor excursion lights a
	// warning icon before the reading.
	fields: [openAlertIconField(true), temperatureField()],
	sortable: false,
	width: 80,
});

/**
 * PD-15417 — open sensor-alert badge. `alertSummary` is only set when the
 * unit has an open excursion ("Temperature", "Offline", "2 alerts"), so a
 * presence check lights the cell; healthy rows stay blank.
 */
export const createAlertsColumn = (): ListBuilderColumn => ({
	id: 'alerts',
	headers: [{ id: 'alerts-header', text: 'Alerts' }],
	fields: [
		{
			id: 'alerts-field',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'alertSummary',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: { fieldName: 'alertSummary', objectTable: 'merchandiser' },
						},
					},
				],
				defaultDataConfig: { type: 'custom_text', customText: '' },
			},
			styleConfig: {
				size: 'sm',
				fontWeight: 'medium',
				colorRule: { type: 'static', staticColor: { type: 'warning' } },
			},
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 110,
});

/**
 * PD-15733 — managed sensor-driven health badge. Derived server-side from
 * the unit's open alerts + device attachment: an open offline alert wins,
 * any other open alert needs attention, a sensored unit with a clean slate
 * is healthy, and a unit with no sensor shows its own quiet state.
 */
export const createHealthColumn = (): ListBuilderColumn => ({
	id: 'health',
	headers: [{ id: 'health-header', text: 'Health' }],
	fields: [healthBadgeField()],
	sortable: false,
	width: 140,
});

/** Stock level (Empty / Low / Medium / Full). */
export const createStockLevelColumn = (): ListBuilderColumn => ({
	id: 'stock-level',
	headers: [{ id: 'stock-level-header', text: 'Stock' }],
	fields: [
		{
			id: 'stock-level-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'stockLevel', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 110,
});

/**
 * PD-16707 — the last 90 days of revenue at the customer location the unit
 * sits at (summed per placement address server-side), so staff can see which
 * placements earn their keep. A warehouse or unplaced unit has no customer
 * location and shows a dash.
 *
 * Sortable like Fill %: the value is computed after the query, so the backend
 * orders the whole filtered set in memory before paginating, with the dash
 * rows last in both directions. The header click defaults to high to low
 * because "which locations earn the most" is the question being asked.
 */
export const createLocationRevenueColumn = (): ListBuilderColumn => ({
	id: 'location-revenue',
	headers: [
		{ id: 'location-revenue-header', text: '90-Day Location Revenue' },
	],
	fields: [
		{
			id: 'location-revenue-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'locationRevenue90',
					objectTable: 'merchandiser',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: {
					numberFormat: 'currency',
					decimalPlaces: 2,
					currencySymbol: '$',
					currencyPosition: 'prefix',
					thousandSeparator: true,
				},
			},
			displayStyle: 'currency',
		},
	],
	sortable: true,
	sortValueType: 'number',
	sortFields: [
		{
			fieldName: 'locationRevenue90',
			direction: 'DESC',
			objectTable: 'merchandiser',
		},
	],
	alignment: 'right',
	// Wide enough for the one-line header plus its sort icon (~212px). The
	// table is table-fixed and sizes its flexible columns from the configured
	// widths, so a width narrower than the header pushed the whole table about
	// 90px past its container at every screen width.
	width: 220,
});

/*
 * PD-16707 — the stacked columns. At Motor City Ice's usual screen width the
 * ten single-value columns ran the table past the right edge, hiding the
 * revenue column. The owner asked for fewer, stacked columns, so the values
 * that are read together now share a cell: Status over Health, the sensor id
 * over its reading and the location count, Fill % over Qty Needed.
 *
 * Fixed widths, each at least as wide as its header (with the sort icon,
 * where there is one) and its widest usual value. A configured width
 * narrower than the header makes the table-fixed layout overflow its
 * container at every screen width (see the revenue column).
 */

/**
 * Status badge with the sensor-driven health badge under it, so "Active but
 * blind" still reads at a glance (PD-15733).
 */
export const createStatusHealthColumn = (): ListBuilderColumn => ({
	id: 'status-health',
	headers: [
		{ id: 'status-health-header', text: 'Status' },
		{ id: 'status-health-subheader', text: 'Health' },
	],
	fields: [
		statusBadgeField(),
		lineBreakField('status-health-break'),
		{
			...healthBadgeField(),
			// A unit with no sensor has no health to report. The Sensor column
			// already shows its dash, so the line is left out rather than
			// repeating it as a second dash.
			conditionalVisibility: {
				field: 'healthState',
				condition: 'is_not_null',
			},
		},
	],
	sortable: false,
	// "Needs Attention" is the widest badge.
	width: 150,
});

/**
 * The sensor's device id, then its current temperature when there is one,
 * then how many locations the unit has, muted: "2 locations".
 *
 * No open-alert icon here (PD-16707): the Health badge beside it already says
 * Needs Attention or Offline, and on its own line the icon showed even for a
 * unit with no sensor or no reading, where it pointed at nothing.
 */
export const createSensorSummaryColumn = (): ListBuilderColumn => ({
	id: 'sensor-summary',
	headers: [{ id: 'sensor-summary-header', text: 'Sensor' }],
	fields: [
		sensorDeviceField(),
		// The reading line appears only with a reading. A unit without a
		// sensor shows its dash on the line above, and one whose sensor has
		// stopped reporting is flagged Offline by its Health badge, so a dash
		// here would say nothing more.
		{
			...lineBreakField('sensor-summary-temperature-break'),
			conditionalVisibility: {
				field: 'currentTemperature',
				condition: 'is_not_null',
			},
		},
		{
			...temperatureField(),
			conditionalVisibility: {
				field: 'currentTemperature',
				condition: 'is_not_null',
			},
		},
		lineBreakField('sensor-summary-locations-break'),
		{
			id: 'sensor-summary-locations',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'locationsCount',
						// Compared as strings by the conditional resolver.
						operator: 'equals',
						value: '1',
						dataConfig: { type: 'custom_text', customText: '1 location' },
					},
					{
						field: 'locationsCount',
						operator: 'not_equals',
						value: '0',
						dataConfig: {
							type: 'template',
							template: '{locationsCount} locations',
							templateFields: [
								{ fieldName: 'locationsCount', objectTable: 'merchandiser' },
							],
						},
					},
				],
				// None configured (or unknown): the line is left out. The
				// Locations column shows a dash for the same unit because "0"
				// reads as a measurement; a bare dash on a third line would not
				// say what it stands for.
				defaultDataConfig: { type: 'custom_text', customText: '' },
			},
			styleConfig: secondaryLineStyle(),
			displayStyle: 'text',
		},
	],
	sortable: false,
	// A full device id ("MPY26CK4U0036960P") on one line.
	width: 170,
});

/**
 * Fill % (followed by the needs-refill icon) over the units needed, so "how
 * empty" and "how much to bring" still read together (PD-16059). The header sorts
 * by Fill %, the same sort the single Fill % column had; Qty Needed stays
 * sortable from the sort dropdown (see the configuration's tableSorts).
 */
export const createFillSummaryColumn = (): ListBuilderColumn => ({
	id: 'fill-summary',
	headers: [
		{ id: 'fill-summary-header', text: 'Fill %' },
		{ id: 'fill-summary-subheader', text: 'Qty Needed' },
	],
	fields: [
		fillPercentField(),
		needsRefillIconField(false),
		lineBreakField('fill-summary-break'),
		{
			...quantityNeededField(),
			styleConfig: secondaryLineStyle(),
			prefix: 'Qty needed ',
			// Unknowable (no sized bins) already shows as the dash above.
			conditionalVisibility: {
				field: 'quantityNeeded',
				condition: 'is_not_null',
			},
		},
	],
	sortable: true,
	sortFields: fillSortFields(),
	// The cell's first value renders as text, so the sort dropdown cannot read
	// "number" from it; said outright so it offers Low/High, not A-Z.
	sortValueType: 'number',
	// The "Qty Needed" sub-header plus the sort icon.
	width: 130,
});

// Alerts + Stock dropped as separate columns (owner request, 2026-08-24):
// their signal now rides as warning icons on the temperature and Fill %.
//
// PD-16707 — Customer Name leads, then the unit: the order Motor City Ice had
// set for itself (the tenant that runs merchandisers), now the standard. The
// middle columns are stacked (see above) so the table fits at common screen
// widths. The single-value factories above remain exported, and their fields
// stay in the configuration's field picker, so a layout can still show any of
// them as its own column.
export const createMerchandiserColumns = (): Array<ListBuilderColumn> => [
	createPlacementColumn(),
	createMerchandiserNameColumn(),
	createStatusHealthColumn(),
	createSensorSummaryColumn(),
	createFillSummaryColumn(),
	createLocationRevenueColumn(),
];
